import React, { useState, useEffect } from 'react';
import { FaSearch, FaFilter, FaArrowLeft, FaArrowRight } from 'react-icons/fa';

const Books = () => {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [availabilityFilter, setAvailabilityFilter] = useState('');
  const [featuredFilter, setFeaturedFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const booksPerPage = 6;

  useEffect(() => {
    const fetchBooks = async () => {
      try {
        let url = `https://my-portfoli-backed.onrender.com/api/books?page=${currentPage}&limit=${booksPerPage}`;
        
        if (searchTerm) {
          url += `&search=${searchTerm}`;
        }
        
        if (availabilityFilter) {
          url += `&publishStatus=${availabilityFilter === 'available' ? 'متاح' : 'قريباً'}`;
        }
        
        if (featuredFilter) {
          url += `&featured=true`;
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          mode: 'cors'
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        setBooks(data.data.books);
        setTotalItems(data.totalItems);
      } catch (err) {
        console.error('Error details:', err);
        setError('حدث خطأ في جلب البيانات. تأكد من اتصال الخادم');
      } finally {
        setLoading(false);
      }
    };

    fetchBooks();
  }, [searchTerm, availabilityFilter, featuredFilter, currentPage]);

  const totalPages = Math.ceil(totalItems / booksPerPage);

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1); // العودة للصفحة الأولى عند إجراء بحث جديد
  };

  const handleAvailabilityFilter = (filter) => {
    setAvailabilityFilter(filter);
    setCurrentPage(1); // العودة للصفحة الأولى عند تغيير الفلتر
  };

  const toggleFeaturedFilter = () => {
    setFeaturedFilter(!featuredFilter);
    setCurrentPage(1); // العودة للصفحة الأولى عند تغيير الفلتر
  };

  const styles = {
    container: {
      backgroundColor: '#ffffff',
      color: '#333333',
      minHeight: '100vh',
      padding: '4rem 2rem',
      fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      lineHeight: '1.6'
    },
    content: {
      maxWidth: '900px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: '3rem',
      borderBottom: '2px solid #000000',
      paddingBottom: '1.5rem'
    },
    title: {
      fontSize: '2.5rem',
      fontWeight: '700',
      color: '#000000',
      marginBottom: '0.5rem'
    },
    subtitle: {
      fontSize: '1.2rem',
      color: '#555555',
      fontWeight: '300'
    },
    controls: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem',
      flexWrap: 'wrap',
      gap: '1rem'
    },
    searchContainer: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: '250px'
    },
    searchInput: {
      flex: 1,
      padding: '0.5rem 1rem',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '1rem',
      marginRight: '0.5rem'
    },
    searchButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#000',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    filterButtons: {
      display: 'flex',
      gap: '0.5rem'
    },
    filterButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f0f0f0',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    activeFilter: {
      backgroundColor: '#000',
      color: '#fff'
    },
    booksGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '2rem',
      marginTop: '2rem'
    },
    bookCard: {
      border: '1px solid #eeeeee',
      borderRadius: '8px',
      overflow: 'hidden',
      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
      ':hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 10px 20px rgba(0,0,0,0.1)'
      }
    },
    bookCover: {
      width: '100%',
      height: '380px',
      backgroundColor: '#f5f5f5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#999999',
      position: 'relative',
      overflow: 'hidden'
    },
    bookBadge: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      backgroundColor: '#000000',
      color: '#ffffff',
      padding: '0.3rem 0.8rem',
      borderRadius: '4px',
      fontSize: '0.8rem',
      fontWeight: '500'
    },
    upcomingBadge: {
      backgroundColor: '#555555'
    },
    bookContent: {
      padding: '1.5rem'
    },
    bookTitle: {
      fontSize: '1.3rem',
      fontWeight: '600',
      marginBottom: '0.5rem',
      color: '#000000'
    },
    bookAuthor: {
      fontSize: '1rem',
      color: '#555555',
      marginBottom: '0.5rem'
    },
    bookMeta: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: '1rem',
      fontSize: '0.9rem',
      color: '#777777'
    },
    bookDescription: {
      marginBottom: '1.5rem',
      color: '#555555',
      fontSize: '0.95rem'
    },
    bookLinks: {
      display: 'flex',
      gap: '1rem'
    },
    purchaseButton: {
      flex: 1,
      padding: '0.7rem',
      borderRadius: '4px',
      textDecoration: 'none',
      fontSize: '0.9rem',
      fontWeight: '500',
      textAlign: 'center',
      transition: 'background-color 0.3s ease'
    },
    primaryButton: {
      backgroundColor: '#000000',
      color: '#ffffff',
      ':hover': {
        backgroundColor: '#333333'
      }
    },
    secondaryButton: {
      backgroundColor: '#f5f5f5',
      color: '#333333',
      border: '1px solid #dddddd',
      ':hover': {
        backgroundColor: '#eeeeee'
      }
    },
    sectionTitle: {
      fontSize: '1.8rem',
      fontWeight: '600',
      color: '#000000',
      borderBottom: '1px solid #eeeeee',
      paddingBottom: '0.5rem',
      margin: '3rem 0 1.5rem'
    },
    loading: {
      textAlign: 'center',
      padding: '2rem',
      fontSize: '1.2rem'
    },
    error: {
      textAlign: 'center',
      padding: '2rem',
      color: 'red',
      fontSize: '1.2rem'
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '2rem'
    },
    pageButton: {
      padding: '0.5rem 1rem',
      backgroundColor: '#f0f0f0',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    activePage: {
      backgroundColor: '#000',
      color: '#fff'
    },
    disabledButton: {
      opacity: '0.5',
      cursor: 'not-allowed'
    },
    noResults: {
      textAlign: 'center',
      padding: '2rem',
      gridColumn: '1 / -1'
    }
  };

  if (loading) {
    return <div style={styles.loading}>جاري تحميل الكتب...</div>;
  }

  if (error) {
    return <div style={styles.error}>حدث خطأ: {error}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.content}>
      <header style={styles.header}>
  <h1 style={styles.title}>كتبي</h1>
  <p style={styles.subtitle}>كتبي في البرمجة وتطوير البرمجيات</p>
</header>

        <div style={styles.controls}>
          <form onSubmit={handleSearch} style={styles.searchContainer}>
            <input
              type="text"
              placeholder="ابحث في الكتب..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={styles.searchInput}
            />
            <button type="submit" style={styles.searchButton}>
              <FaSearch /> بحث
            </button>
          </form>

          <div style={styles.filterButtons}>
            <button
              style={{
                ...styles.filterButton,
                ...(availabilityFilter === 'available' ? styles.activeFilter : {})
              }}
              onClick={() => handleAvailabilityFilter('available')}
            >
              <FaFilter /> متاحة
            </button>
            <button
              style={{
                ...styles.filterButton,
                ...(availabilityFilter === 'upcoming' ? styles.activeFilter : {})
              }}
              onClick={() => handleAvailabilityFilter('upcoming')}
            >
              <FaFilter /> قريبًا
            </button>
            <button
              style={{
                ...styles.filterButton,
                ...(featuredFilter ? styles.activeFilter : {})
              }}
              onClick={toggleFeaturedFilter}
            >
              <FaFilter /> مميزة
            </button>
            {(availabilityFilter || featuredFilter) && (
              <button
                style={styles.filterButton}
                onClick={() => {
                  setAvailabilityFilter('');
                  setFeaturedFilter(false);
                }}
              >
                إلغاء الفلتر
              </button>
            )}
          </div>
        </div>

        <div style={styles.booksGrid}>
          {books.length > 0 ? (
            books.map((book) => (
              <div key={book._id} style={styles.bookCard}>
                <div style={{
                  ...styles.bookCover,
                  backgroundImage: book.coverImage ? `url(${book.coverImage.viewLink.replace('/view?usp=drivesdk', '/preview')})` : 'none',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }}>
                  {!book.coverImage && book.title}
                  {book.meta?.isFeatured && <span style={styles.bookBadge}>مميز</span>}
                  {book.publishStatus !== 'متاح' && (
                    <span style={{...styles.bookBadge, ...styles.upcomingBadge}}>قريبًا</span>
                  )}
                </div>
                <div style={styles.bookContent}>
                  <h3 style={styles.bookTitle}>{book.title}</h3>
                  <p style={styles.bookAuthor}>تأليف: {book.author}</p>
                  
                  <div style={styles.bookMeta}>
                    {book.publishStatus === 'متاح' ? (
                      <>
                        <span>الإصدار: {book.edition}</span>
                        <span>{book.pages} صفحة</span>
                        <span>{book.bookType}</span>
                      </>
                    ) : (
                      <span>متوقع: {new Date(book.expectedPublishDate).toLocaleDateString('ar-EG')}</span>
                    )}
                  </div>
                  
                  <p style={styles.bookDescription}>{book.description}</p>
                  
                  <div style={styles.bookLinks}>
                    {book.publishStatus === 'متاح' ? (
                      book.purchaseLink ? (
                        <a 
                          href={book.purchaseLink} 
                          style={{...styles.purchaseButton, ...styles.primaryButton}}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          شراء الكتاب
                        </a>
                      ) : (
                        <button disabled style={{...styles.purchaseButton, ...styles.secondaryButton, opacity: 0.7}}>
                          غير متاح للشراء
                        </button>
                      )
                    ) : (
                      <button disabled style={{...styles.purchaseButton, ...styles.secondaryButton, opacity: 0.7, cursor: 'not-allowed'}}>
                        سيتم الإعلان قريبًا
                      </button>
                    )}
                    
                    {book.publishStatus === 'متاح' && book.downloadable && book.pdfFile && (
                      <a 
                        href={book.pdfFile.downloadLink} 
                        style={{...styles.purchaseButton, ...styles.secondaryButton}}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        تحميل PDF
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div style={styles.noResults}>
              لا توجد كتب متطابقة مع معايير البحث
            </div>
          )}
        </div>

        {totalItems > booksPerPage && (
          <div style={styles.pagination}>
            <button
              style={{
                ...styles.pageButton,
                ...(currentPage === 1 ? styles.disabledButton : {})
              }}
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <FaArrowLeft /> السابق
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i + 1}
                style={{
                  ...styles.pageButton,
                  ...(currentPage === i + 1 ? styles.activePage : {})
                }}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            
            <button
              style={{
                ...styles.pageButton,
                ...(currentPage === totalPages ? styles.disabledButton : {})
              }}
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              التالي <FaArrowRight />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Books;